import React, { useEffect, useMemo, useState } from 'react';
import { Edit, Package, Plus, RefreshCw, Search, Star, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Product } from '../../types';
import apiService from '../../services/api';
import { getPricingConfig, savePricingConfig } from '../../utils/pricingConfig';
import { toProductImageUrl } from '../../utils/imageUrl';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';

const STATUS_BADGE: Record<string, string> = {
  activo: 'bg-green-100 text-green-700 border-green-200',
  inactivo: 'bg-yellow-100 text-yellow-700 border-yellow-200'
};

const CATEGORY_LABEL: Record<string, string> = {
  comercial: 'Comercial',
  industrial: 'Industrial',
  domestico: 'Doméstico'
};

const CATEGORY_BADGE: Record<string, string> = {
  comercial: 'bg-blue-100 text-blue-700 border-blue-200',
  industrial: 'bg-green-100 text-green-700 border-green-200',
  domestico: 'bg-purple-100 text-purple-700 border-purple-200'
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const StatusBadge: React.FC<{ status: 'activo' | 'inactivo' }> = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${STATUS_BADGE[status]}`}>
    {status === 'activo' ? 'Activo' : 'Inactivo'}
  </span>
);

const DeleteConfirm: React.FC<{ onConfirm: () => void; onCancel: () => void }> = ({ onConfirm, onCancel }) => (
  <div className="inline-flex items-center gap-2 text-xs">
    <span className="text-gray-600">¿Confirmar?</span>
    <button onClick={onConfirm} className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700">Sí</button>
    <button onClick={onCancel} className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50">No</button>
  </div>
);

const SkeletonRows: React.FC = () => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse space-y-3">
    {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
  </div>
);

const EmptyState: React.FC<{ withFilters: boolean }> = ({ withFilters }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
    <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
    <p className="text-gray-800 font-medium">{withFilters ? 'No hay resultados para los filtros aplicados' : 'No hay productos cargados'}</p>
    <p className="text-sm text-gray-500 mt-1">{withFilters ? 'Probá modificar filtros o búsqueda.' : 'Creá un producto para comenzar.'}</p>
  </div>
);

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [arsRate, setArsRate] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  useBodyScrollLock(!!selectedProduct);
  useEscapeToClose(!!selectedProduct, () => setSelectedProduct(null));

  const fetchProducts = async (manualRetry = false) => {
    if (manualRetry) setIsRetrying(true);
    try {
      setLoadError(null);
      const cfg = getPricingConfig();
      const savedDollar = Number(cfg.valorDolar || 0);
      const [response, currency] = await Promise.all([
        apiService.getProducts({ includeInactive: true }),
        apiService.getCurrencyRates().catch(() => null)
      ]);
      setProducts(response.products || []);
      if (currency?.rates?.sell) {
        const sell = Number(currency.rates.sell);
        setArsRate(sell);
        if (sell > 0 && sell !== savedDollar) savePricingConfig({ ...cfg, valorDolar: sell });
      } else if (savedDollar > 0) {
        setArsRate(savedDollar);
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setLoadError(detail || 'No se pudieron cargar los productos.');
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      if (selectedCategory !== 'all' && product.category !== selectedCategory) return false;
      if (selectedStatus !== 'all') {
        const isActive = selectedStatus === 'active';
        if (product.isActive !== isActive) return false;
      }
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        product.name.toLowerCase().includes(q) ||
        (product.description || '').toLowerCase().includes(q) ||
        product.material.toLowerCase().includes(q)
      );
    });
  }, [products, selectedCategory, selectedStatus, searchTerm]);

  const inactiveCount = useMemo(() => products.filter((p) => !p.isActive).length, [products]);

  const getUnitsPerPack = (productName?: string) => {
    const match = String(productName || '').match(/x\s*(\d+)\s*unidades?/i);
    const units = match ? Number(match[1]) : 1;
    return Number.isFinite(units) && units > 0 ? units : 1;
  };

  const formatPackPriceARS = (product: Product) => {
    const usd = Number(product.priceUSD || 0);
    if (!usd) return 'Consultar precio';
    if (!arsRate) return 'Cargando ARS...';
    const ars = usd * getUnitsPerPack(product.name) * arsRate;
    return `$${Math.round(ars).toLocaleString('es-AR')} ARS por pack`;
  };

  const toggleStatus = async (productId: number) => {
    try {
      setInlineError(null);
      const current = products.find((p) => p.id === productId);
      if (!current) return;
      const response = await apiService.updateProduct(productId, { ...current, isActive: !current.isActive });
      setProducts((prev) => prev.map((p) => (p.id === productId ? response.product : p)));
      setSelectedProduct((prev) => (prev && prev.id === productId ? response.product : prev));
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setInlineError(detail || 'No se pudo actualizar el estado del producto.');
    }
  };

  const deleteProduct = async (productId: number) => {
    try {
      setInlineError(null);
      await apiService.deleteProduct(productId);
      setProducts((prev) => prev.filter((p) => p.id !== productId));
      setSelectedProduct((prev) => (prev?.id === productId ? null : prev));
      setConfirmDeleteId(null);
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setInlineError(detail || 'No se pudo eliminar el producto.');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Productos</h1>
          <p className="text-gray-600">Cargando información...</p>
        </div>
        <SkeletonRows />
      </div>
    );
  }

  const hasFilters = selectedCategory !== 'all' || selectedStatus !== 'all' || searchTerm.trim().length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Gestión de Productos</h1>
          <p className="text-sm text-gray-600 sm:text-base">{products.length} productos en total · {inactiveCount} inactivos</p>
        </div>
        <Link to="/admin/productos/nuevo" className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
          <Plus className="h-5 w-5 mr-2" />
          Nuevo Producto
        </Link>
      </div>

      {loadError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 flex items-center justify-between gap-4">
          <span>{loadError}</span>
          <button
            onClick={() => fetchProducts(true)}
            disabled={isRetrying}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-red-300 hover:bg-red-100 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
            Reintentar
          </button>
        </div>
      )}

      {inlineError && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{inlineError}</div>}

      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        <div className="relative col-span-2 md:col-span-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300">
          <option value="all">Todas las categorías</option>
          <option value="comercial">Comercial</option>
          <option value="industrial">Industrial</option>
          <option value="domestico">Doméstico</option>
        </select>
        <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300">
          <option value="all">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="inactive">Inactivo</option>
        </select>
      </div>

      {filteredProducts.length === 0 ? (
        <EmptyState withFilters={hasFilters} />
      ) : (
        <>
        <div className="divide-y rounded-xl border border-gray-100 bg-white shadow-sm md:hidden">
          {filteredProducts.map((product) => (
            <div key={product.id} className={`space-y-2 p-4 ${!product.isActive ? 'border-l-2 border-l-red-400' : ''}`}>
              <button onClick={() => setSelectedProduct(product)} className="flex w-full items-center gap-3 text-left">
                <div className="h-12 w-12 shrink-0 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                  {product.image ? <img src={toProductImageUrl(product.image)} alt={product.name} className="w-full h-full object-cover" /> : <Package className="h-5 w-5 text-gray-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-gray-900">{product.name}</p>
                  <p className="text-xs text-gray-500">{Math.round(Number(product.width) * 100)} cm x {Math.round(Number(product.length) * 100)} cm · {formatPackPriceARS(product)}</p>
                </div>
              </button>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${CATEGORY_BADGE[product.category] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>{CATEGORY_LABEL[product.category] || product.category}</span>
                <StatusBadge status={product.isActive ? 'activo' : 'inactivo'} />
                {product.isFeatured && <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border bg-green-100 text-green-700 border-green-200"><Star className="h-3 w-3 mr-1" />Destacado</span>}
              </div>
              {confirmDeleteId === product.id ? (
                <DeleteConfirm onConfirm={() => deleteProduct(product.id)} onCancel={() => setConfirmDeleteId(null)} />
              ) : (
                <div className="flex gap-2">
                  <Link to={`/admin/productos/${product.id}/editar`} className="inline-flex flex-1 items-center justify-center gap-1 rounded border border-amber-200 px-2 py-1.5 text-xs text-amber-700 hover:bg-amber-50"><Edit className="h-3.5 w-3.5" />Editar</Link>
                  <button onClick={() => toggleStatus(product.id)} className="flex-1 px-2 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50">{product.isActive ? 'Desactivar' : 'Activar'}</button>
                  <button onClick={() => setConfirmDeleteId(product.id)} className="flex-1 px-2 py-1.5 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50">Eliminar</button>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Producto</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Categoría</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Precio</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Estado</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Fecha alta</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr
                  key={product.id}
                  onClick={() => setSelectedProduct(product)}
                  className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${!product.isActive ? 'border-l-2 border-l-red-400' : ''}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                        {product.image ? <img src={toProductImageUrl(product.image)} alt={product.name} className="w-full h-full object-cover" /> : <Package className="h-5 w-5 text-gray-500" />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-xs text-gray-500">{Math.round(Number(product.width) * 100)} cm x {Math.round(Number(product.length) * 100)} cm</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${CATEGORY_BADGE[product.category] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                      {CATEGORY_LABEL[product.category] || product.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">{formatPackPriceARS(product)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={product.isActive ? 'activo' : 'inactivo'} />
                      {product.isFeatured && <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border bg-green-100 text-green-700 border-green-200"><Star className="h-3 w-3 mr-1" />Destacado</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(product.createdAt)}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {confirmDeleteId === product.id ? (
                      <DeleteConfirm onConfirm={() => deleteProduct(product.id)} onCancel={() => setConfirmDeleteId(null)} />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Link to={`/admin/productos/${product.id}/editar`} className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded" title="Editar">
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button onClick={() => toggleStatus(product.id)} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">
                          {product.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                        <button onClick={() => setConfirmDeleteId(product.id)} className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded" title="Eliminar">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {selectedProduct && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Detalle del producto</h2>
              <button onClick={() => setSelectedProduct(null)} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nombre</p>
                  <p className="font-medium text-gray-900">{selectedProduct.name}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Categoría</p>
                  <p className="font-medium text-gray-900">{CATEGORY_LABEL[selectedProduct.category] || selectedProduct.category}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Material</p>
                  <p className="font-medium text-gray-900">{selectedProduct.material}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Grosor</p>
                  <p className="font-medium text-gray-900">{selectedProduct.thickness} μ</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Medida</p>
                  <p className="font-medium text-gray-900">{Math.round(Number(selectedProduct.width) * 100)} cm x {Math.round(Number(selectedProduct.length) * 100)} cm</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cantidad mínima</p>
                  <p className="font-medium text-gray-900">{selectedProduct.minQuantity.toLocaleString()} unidades</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Estado</p>
                  <div className="mt-1"><StatusBadge status={selectedProduct.isActive ? 'activo' : 'inactivo'} /></div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fecha alta</p>
                  <p className="font-medium text-gray-900">{formatDateTime(selectedProduct.createdAt)}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Descripción</p>
                  <p className="font-medium text-gray-900">{selectedProduct.description || '-'}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {confirmDeleteId === selectedProduct.id ? (
                  <DeleteConfirm onConfirm={() => deleteProduct(selectedProduct.id)} onCancel={() => setConfirmDeleteId(null)} />
                ) : (
                  <button onClick={() => setConfirmDeleteId(selectedProduct.id)} className="px-3 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50">
                    Eliminar
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setSelectedProduct(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cerrar</button>
                <Link to={`/admin/productos/${selectedProduct.id}/editar`} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Editar</Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
